import type { QueryClient } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from "@tanstack/react-router";
import { z } from "zod";
import { landingFor, sessionQuery } from "./auth/session.js";
import { AdminAuditPage } from "./routes/admin/audit.js";
import { AdminBillingPage } from "./routes/admin/billing.js";
import { CheckoutPage } from "./routes/admin/checkout.js";
import { DomainPage } from "./routes/admin/domain.js";
import { OrderPage } from "./routes/admin/order.js";
import { AdminOverviewPage } from "./routes/admin/overview.js";
import { AdminUsersPage } from "./routes/admin/users.js";
import { AdminShell } from "./routes/admin-shell.js";
import { AppShell } from "./routes/app-shell.js";
import { AcceptInvitePage } from "./routes/auth/accept-invite.js";
import { ForgotPasswordPage } from "./routes/auth/forgot-password.js";
import { LoginPage } from "./routes/auth/login.js";
import { MfaPage } from "./routes/auth/mfa.js";
import { ResetPasswordPage } from "./routes/auth/reset-password.js";
import { SignupPage } from "./routes/auth/signup.js";
import { VerifyEmailPage } from "./routes/auth/verify-email.js";
import { CalendarPage } from "./routes/calendar.js";
import { ContactsPage } from "./routes/contacts.js";
import { DomainsPage } from "./routes/domains.js";
import { RouteErrorPage } from "./routes/error.js";
import { LegalPage } from "./routes/legal.js";
import { MailPage } from "./routes/mail.js";
import { NotesPage } from "./routes/notes.js";
import { PricingPage } from "./routes/pricing.js";
import { DevicesPage } from "./routes/settings/devices.js";
import { MailSettingsPage } from "./routes/settings/mail-settings.js";
import { NotificationsPage } from "./routes/settings/notifications.js";
import { SecurityPage } from "./routes/settings/security.js";
import { StorePage } from "./routes/store.js";
import { TasksPage } from "./routes/tasks.js";

export interface RouterContext {
  queryClient: QueryClient;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: Outlet,
  errorComponent: RouteErrorPage,
});

// --- Public routes: send signed-in users on to the app. -------------------------------------------

const publicRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "public",
  beforeLoad: async ({ context }) => {
    const session = await context.queryClient.ensureQueryData(sessionQuery);
    if (session) {
      throw redirect({ to: landingFor(session) });
    }
  },
  component: Outlet,
});

/** The storefront is open to everyone; a signed-in visitor sees links into the workspace. */
const storeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: StorePage,
});
// Public pages open to everyone, signed in or not.
const pricingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/pricing",
  component: PricingPage,
});
const legalRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/legal/$doc",
  component: LegalPage,
});
/** "/legal" on its own has no page of its own; the terms are where the documents start. */
const legalIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/legal",
  beforeLoad: () => {
    throw redirect({ to: "/legal/$doc", params: { doc: "terms" } });
  },
});
const loginRoute = createRoute({
  getParentRoute: () => publicRoute,
  path: "/login",
  validateSearch: z.object({ domain: z.string().optional() }),
  component: LoginPage,
});
const signupRoute = createRoute({
  getParentRoute: () => publicRoute,
  path: "/signup",
  validateSearch: z.object({ domain: z.string().optional() }),
  component: SignupPage,
});

/** The invite page is reachable while signed out; a signed-in user must sign out first. */
const acceptInviteRoute = createRoute({
  getParentRoute: () => publicRoute,
  path: "/accept-invite",
  validateSearch: z.object({ token: z.string().default("") }),
  component: AcceptInvitePage,
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => publicRoute,
  path: "/forgot-password",
  component: ForgotPasswordPage,
});

// --- Link landing pages: reachable whether or not the visitor is signed in. --------------------------

const tokenSearch = z.object({ token: z.string().default("") });

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reset-password",
  validateSearch: tokenSearch,
  component: ResetPasswordPage,
});

const verifyEmailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/verify-email",
  validateSearch: tokenSearch,
  component: VerifyEmailPage,
});

// --- Second factor: signed in, but the session still owes its code. -------------------------------

const mfaRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/mfa",
  beforeLoad: async ({ context }) => {
    const session = await context.queryClient.ensureQueryData(sessionQuery);
    if (!session) {
      throw redirect({ to: "/login" });
    }
    if (!session.mfa.pending) {
      throw redirect({ to: "/mail" });
    }
  },
  component: MfaPage,
});

// --- Authenticated application. -------------------------------------------------------------------

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "app",
  beforeLoad: async ({ context }) => {
    const session = await context.queryClient.ensureQueryData(sessionQuery);
    if (!session) {
      throw redirect({ to: "/login" });
    }
    if (session.mfa.pending) {
      throw redirect({ to: "/mfa" });
    }
    return { session };
  },
  component: AppShell,
});

const mailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/mail",
  validateSearch: z.object({ email: z.string().optional(), to: z.string().optional() }),
  component: MailPage,
});
const notesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/notes",
  validateSearch: z.object({
    shared: z.enum(["1"]).optional(),
    notebook: z.string().optional(),
    tag: z.string().optional(),
    note: z.string().optional(),
  }),
  component: NotesPage,
});
const tasksRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/tasks",
  validateSearch: z.object({ list: z.string().optional(), task: z.string().optional() }),
  component: TasksPage,
});
const calendarRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/calendar",
  component: CalendarPage,
});
const contactsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/contacts",
  component: ContactsPage,
});
/** Old workspace paths keep working and land in the console. */
const legacyDomainsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/domains",
  beforeLoad: () => {
    throw redirect({ to: "/admin/domains" });
  },
});
const legacyTeamRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings/team",
  beforeLoad: () => {
    throw redirect({ to: "/admin/users" });
  },
});
const securityRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings/security",
  component: SecurityPage,
});
const mailSettingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings/mail",
  component: MailSettingsPage,
});
const devicesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings/devices",
  component: DevicesPage,
});
const notificationsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings/notifications",
  component: NotificationsPage,
});
// --- Admin console: a separate shell, owners and administrators only. ------------------------------

const adminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin",
  beforeLoad: async ({ context }) => {
    const session = await context.queryClient.ensureQueryData(sessionQuery);
    if (!session) {
      throw redirect({ to: "/login" });
    }
    if (session.mfa.pending) {
      throw redirect({ to: "/mfa" });
    }
    if (session.user.role !== "owner" && session.user.role !== "admin") {
      throw redirect({ to: "/mail" });
    }
    return { session };
  },
  component: AdminShell,
});
const adminIndexRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/",
  component: AdminOverviewPage,
});
const adminUsersRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/users",
  component: AdminUsersPage,
});
const adminDomainsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/domains",
  component: DomainsPage,
});
const adminAuditRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/audit",
  component: AdminAuditPage,
});
const adminBillingRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/billing",
  validateSearch: z.object({ checkout: z.string().optional() }),
  component: AdminBillingPage,
});
const adminCheckoutRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/domains/checkout",
  validateSearch: z.object({
    domain: z.string().default(""),
    years: z.number().int().min(1).max(10).default(1),
  }),
  component: CheckoutPage,
});
const adminDomainRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/domains/$domainId",
  component: DomainPage,
});
const adminOrderRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/orders/$orderId",
  validateSearch: z.object({
    paid: z.coerce.number().optional(),
    cancelled: z.coerce.number().optional(),
  }),
  component: OrderPage,
});

const routeTree = rootRoute.addChildren([
  storeRoute,
  pricingRoute,
  legalIndexRoute,
  legalRoute,
  publicRoute.addChildren([loginRoute, signupRoute, acceptInviteRoute, forgotPasswordRoute]),
  resetPasswordRoute,
  verifyEmailRoute,
  mfaRoute,
  appRoute.addChildren([
    mailRoute,
    calendarRoute,
    tasksRoute,
    notesRoute,
    contactsRoute,
    securityRoute,
    devicesRoute,
    notificationsRoute,
    mailSettingsRoute,
    legacyDomainsRoute,
    legacyTeamRoute,
  ]),
  adminRoute.addChildren([
    adminIndexRoute,
    adminUsersRoute,
    adminDomainsRoute,
    adminCheckoutRoute,
    adminDomainRoute,
    adminOrderRoute,
    adminAuditRoute,
    adminBillingRoute,
  ]),
]);

export function createAppRouter(queryClient: QueryClient) {
  return createRouter({ routeTree, context: { queryClient }, defaultPreload: "intent" });
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
